import {
    ZfAccordion,
    ZfAccordionItem,
    ZfBanner,
    ZfDialog,
    ZfDialogContent,
    ZfDialogFooter,
    ZfDialogHeader,
    ZfFormField,
    ZfGrid,
    ZfInput,
    ZfLink,
    ZfSelectOption,
    ZfStack,
    ZfTextButton,
    ZfTypography,
} from '@zafire/react';
import moment from 'moment';
import type { FunctionComponent } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useClient } from '../../../api';
import type { BalanceEntryDto } from '../../../api/generated/client';
import { useYwuApp } from '../../../contexts/ywu-app-context';
import { useWealthPlanningNavigate } from '../../../hooks/useWealthPlanningNavigate';
import { wnSendNavigate } from '../../../utils/wn-event-utils';

interface IProps {
    item?: BalanceEntryDto;
    isOpen: boolean;
    children?: never;
    onClose: () => void;
}

export const BalanceEntryReadModal: FunctionComponent<IProps> = ({ item, isOpen, onClose }) => {
    const { t } = useTranslation();
    const { isWealthNavigatorMFE } = useYwuApp();
    const { clientUuid, isOpenedInClientContext } = useWealthPlanningNavigate();
    const { data: client } = useClient();

    const initialCurrency = item?.referenceCurrency || client?.currency;

    const assetClassBreakdownItems = [
        {
            value: item?.assetClassBreakdown?.cashAmount ? Math.round(item.assetClassBreakdown.cashAmount) : 0,
            label: t('wealthAssetClassCashAndShortTerm'),
        },
        {
            value: item?.assetClassBreakdown?.bondsAmount ? Math.round(item?.assetClassBreakdown?.bondsAmount) : 0,
            label: t('wealthAssetClassBondsAndSimilar'),
        },
        {
            value: item?.assetClassBreakdown?.equitiesAmount ? Math.round(item?.assetClassBreakdown?.equitiesAmount) : 0,
            label: t('wealthAssetClassEquitiesAndSimilar'),
        },
        {
            value: item?.assetClassBreakdown?.alternativeAmount ? Math.round(item?.assetClassBreakdown?.alternativeAmount) : 0,
            label: t('wealthAssetClassAlternativeAndOthers'),
        },
    ];

    const redirectToWNPortfolios = () => wnSendNavigate(`client-data/${clientUuid}/INTL/client-details/portfolio-analysis`);

    return (
        <ZfDialog show={isOpen} size="small" onDialogClose={onClose}>
            <ZfDialogHeader>{t('modalViewTitle')}</ZfDialogHeader>
            <ZfDialogContent>
                <ZfStack direction="column" spacing="medium">
                    <ZfBanner type="info">
                        <ZfTypography variant="body-small" weight="bold">
                            <Trans
                                slot="leading"
                                i18nKey="balanceEntryReadModalBannerInfo"
                                components={[
                                    isWealthNavigatorMFE && isOpenedInClientContext && clientUuid ? (
                                        <ZfLink key="balanceEntryReadModalBannerInfo-link" size="small" onClick={redirectToWNPortfolios} />
                                    ) : (
                                        <></>
                                    ),
                                ]}
                            />
                        </ZfTypography>
                    </ZfBanner>
                    <ZfFormField label={t('wealthItemName')}>
                        <ZfInput value={item?.name ?? ''} readonly={true} />
                    </ZfFormField>
                    <ZfStack direction="row" spacing="medium">
                        <ZfFormField label={t('wealthItemAmount')} style={{ flex: 1 }}>
                            <ZfInput value={Math.round(item?.referenceAmount ?? 0)} readonly={true} type="number" format={true} />
                        </ZfFormField>
                        <ZfFormField label={t('wealthItemCurrency')} style={{ flex: 1 }}>
                            <ZfInput type="select" value={initialCurrency ?? ''} readonly={true}>
                                <ZfSelectOption value={initialCurrency ?? ''} description={initialCurrency ?? ''} />
                            </ZfInput>
                        </ZfFormField>
                    </ZfStack>
                    {item?.fromToDatesAllowed && (
                        <ZfStack direction="column" spacing="medium">
                            <ZfTypography variant="body-large">{t('wealthItemFromToDates')}</ZfTypography>
                            <ZfStack direction="row" spacing="medium">
                                <ZfFormField label={t('wealthItemFromDate')} style={{ flex: 1 }}>
                                    <ZfInput type="date" value={moment(item?.fromDate ?? '01.01.2025').format('MM.YYYY')} readonly={true} />
                                </ZfFormField>
                                <ZfFormField label={t('wealthItemToDate')} style={{ flex: 1 }}>
                                    <ZfInput type="date" value={moment(item?.toDate).format('MM.YYYY')} readonly={true} />
                                </ZfFormField>
                            </ZfStack>
                        </ZfStack>
                    )}
                    {item?.investmentStrategyRequired && (
                        <ZfFormField label={t('wealthInvestmentStrategy')}>
                            <ZfInput type="select" value={item?.investmentStrategy ?? ''} readonly={true}>
                                <ZfSelectOption value="FIXED_INCOME" description={t('wealthInvestmentStrategyFixedIncome')} />
                                <ZfSelectOption value="INCOME" description={t('wealthInvestmentStrategyIncome')} />
                                <ZfSelectOption value="BALANCED" description={t('wealthInvestmentStrategyBalanced')} />
                                <ZfSelectOption value="GROWTH" description={t('wealthInvestmentStrategyGrowth')} />
                                <ZfSelectOption value="CAPITAL_GAIN" description={t('wealthInvestmentStrategyCapitalGain')} />
                            </ZfInput>
                        </ZfFormField>
                    )}
                    {!!item?.assetClassBreakdown && (
                        <ZfAccordion>
                            <ZfAccordionItem titleText={t('wealthAssetClassBreakdown')} state="active" showUnderline={false}>
                                <ZfGrid container={true}>
                                    {assetClassBreakdownItems.map((breakdownItem) => (
                                        <ZfGrid key={breakdownItem.label} item={true} columnSize={6}>
                                            <ZfFormField label={breakdownItem.label} style={{ flex: 1 }}>
                                                <ZfInput value={breakdownItem.value} readonly={true} type="number" format={true} />
                                            </ZfFormField>
                                        </ZfGrid>
                                    ))}
                                </ZfGrid>
                            </ZfAccordionItem>
                        </ZfAccordion>
                    )}
                </ZfStack>
            </ZfDialogContent>
            <ZfDialogFooter>
                <ZfTextButton slot="primary" hierarchy="secondary" onClick={onClose}>
                    {t('modalCloseButton')}
                </ZfTextButton>
            </ZfDialogFooter>
        </ZfDialog>
    );
};
